/**
 * Folder sidebar with tree navigation.
 */
import React, { useState } from 'react';
import { FolderIcon, FolderOpenIcon, PlusIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { MediaFolder, FolderTreeNode } from '@/types/media';
import { buildFolderTree } from '@/types/media';
import { Button } from '@/components/ui';

interface FolderSidebarProps {
  folders: MediaFolder[];
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  onCreateFolder: () => void;
  isLoading?: boolean;
}

interface FolderNodeProps {
  node: FolderTreeNode;
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  level: number;
}

function FolderNode({ node, selectedFolderId, onSelectFolder, level }: FolderNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const isSelected = selectedFolderId === node.id;

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <div>
      <div
        onClick={() => onSelectFolder(node.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onSelectFolder(node.id)}
        className={`
          w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors cursor-pointer
          ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}
        `}
        style={{ paddingLeft: `${12 + level * 16}px` }}
      >
        {hasChildren && (
          <span
            onClick={handleExpandClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleExpandClick(e as unknown as React.MouseEvent)}
            className="p-0.5 hover:bg-gray-200 rounded cursor-pointer"
          >
            <ChevronRightIcon
              className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />
          </span>
        )}
        {!hasChildren && <span className="w-4" />}
        {isSelected ? (
          <FolderOpenIcon className="w-4 h-4 text-blue-500" />
        ) : (
          <FolderIcon className="w-4 h-4 text-gray-400" />
        )}
        <span className="truncate flex-1 text-left">{node.name}</span>
        {node.image_count > 0 && (
          <span className="text-xs text-gray-400">{node.image_count}</span>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <FolderNode
              key={child.id}
              node={child}
              selectedFolderId={selectedFolderId}
              onSelectFolder={onSelectFolder}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function FolderSidebar({
  folders,
  selectedFolderId,
  onSelectFolder,
  onCreateFolder,
  isLoading,
}: FolderSidebarProps) {
  const folderTree = buildFolderTree(folders);

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Folders</h2>
        <Button
          variant="secondary"
          size="sm"
          className="w-full justify-center"
          onClick={onCreateFolder}
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          New Folder
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {/* All Images (root) */}
        <button
          onClick={() => onSelectFolder(null)}
          className={`
            w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors mb-1
            ${selectedFolderId === null ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}
          `}
        >
          <span className="w-4" />
          <FolderIcon className="w-4 h-4 text-gray-400" />
          <span className="flex-1 text-left font-medium">All Images</span>
        </button>

        {isLoading ? (
          <div className="space-y-2 px-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : (
          folderTree.map((node) => (
            <FolderNode
              key={node.id}
              node={node}
              selectedFolderId={selectedFolderId}
              onSelectFolder={onSelectFolder}
              level={0}
            />
          ))
        )}
      </div>
    </div>
  );
}
